import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSubmittals } from "@/hooks/use-submittals";

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "approved":
      return "success";
    case "pending":
    case "draft":
      return "warning";
    case "submitted":
    case "under_review":
      return "secondary";
    case "rejected":
      return "destructive";
    default:
      return "default";
  }
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function SubmittalsPage() {
  const { data, isLoading, isError, error, refetch } = useSubmittals();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error?.message ?? "Failed to load submittals"}</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  const items = data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Submittals</h1>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No submittals found.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Spec Section</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.number ?? "--"}</TableCell>
                  <TableCell>{s.title ?? "--"}</TableCell>
                  <TableCell>{s.specSection ?? "--"}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(s.status)}>{s.status ?? "--"}</Badge>
                  </TableCell>
                  <TableCell>{s.submittedBy ?? "--"}</TableCell>
                  <TableCell>{formatDate(s.dateSubmitted)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
